import { Calendar, ClipboardCheck, TrendingUp, Search, RotateCcw, FileText, ArrowRight } from 'lucide-react';

interface UseCaseCardsProps {
  onMeetingPrep: () => void;
  onMeetingFeedback: () => void;
  onDealAdvice: () => void;
  onLeadIntel: () => void;
  onFollowUpStrategy: () => void;
  onSalesScripts: () => void;
  disabled?: boolean;
}

const useCases = [
  {
    key: 'meeting-prep',
    icon: Calendar,
    title: 'Meeting Prep',
    subtitle: 'Get a full briefing on your prospect and a tailored pitch strategy',
    gradient: 'from-[#1875FF] to-[#4A9FFF]',
    iconBg: 'bg-[#1875FF]/10',
    iconColor: 'text-[#1875FF]',
    borderHover: 'hover:border-[#1875FF]/50',
  },
  {
    key: 'meeting-feedback',
    icon: ClipboardCheck,
    title: 'Meeting Feedback',
    subtitle: 'Analyze your meeting performance and get actionable improvements',
    gradient: 'from-[#05E0D1] to-[#52B5AE]',
    iconBg: 'bg-[#05E0D1]/10',
    iconColor: 'text-[#05E0D1]',
    borderHover: 'hover:border-[#05E0D1]/50',
  },
  {
    key: 'deal-advice',
    icon: TrendingUp,
    title: 'Deal Advice',
    subtitle: 'Get expert guidance on progressing a deal through your pipeline',
    gradient: 'from-[#1875FF] to-[#05E0D1]',
    iconBg: 'bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10',
    iconColor: 'text-[#1875FF]',
    borderHover: 'hover:border-[#05E0D1]/50',
  },
  {
    key: 'lead-intel',
    icon: Search,
    title: 'Lead Info',
    subtitle: 'Get a complete briefing on any lead across all channels and agents',
    gradient: 'from-[#1875FF] to-[#05E0D1]',
    iconBg: 'bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10',
    iconColor: 'text-[#1875FF]',
    borderHover: 'hover:border-[#1875FF]/50',
  },
  {
    key: 'follow-up-strategy',
    icon: RotateCcw,
    title: 'Follow Up Strategy',
    subtitle: 'Get coaching on how to follow up with leads based on their engagement',
    gradient: 'from-[#05E0D1] to-[#1875FF]',
    iconBg: 'bg-[#05E0D1]/10',
    iconColor: 'text-[#05E0D1]',
    borderHover: 'hover:border-[#05E0D1]/50',
  },
  {
    key: 'sales-scripts',
    icon: FileText,
    title: 'Sales Scripts',
    subtitle: 'Get tailored scripts for calls, emails, LinkedIn messages, and more',
    gradient: 'from-[#1875FF] to-[#4A9FFF]',
    iconBg: 'bg-[#1875FF]/10',
    iconColor: 'text-[#1875FF]',
    borderHover: 'hover:border-[#1875FF]/50',
  },
];

export function UseCaseCards({
  onMeetingPrep,
  onMeetingFeedback,
  onDealAdvice,
  onLeadIntel,
  onFollowUpStrategy,
  onSalesScripts,
  disabled,
}: UseCaseCardsProps) {
  const handlers: Record<string, () => void> = {
    'meeting-prep': onMeetingPrep,
    'meeting-feedback': onMeetingFeedback,
    'deal-advice': onDealAdvice,
    'lead-intel': onLeadIntel,
    'follow-up-strategy': onFollowUpStrategy,
    'sales-scripts': onSalesScripts,
  };

  return (
    <div className="mb-8">
      <h3 className="text-sm font-bold text-gray-500 mb-4 uppercase tracking-widest font-sans">
        What can I help you with?
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {useCases.map((uc) => {
          const Icon = uc.icon;
          return (
            <button
              key={uc.key}
              onClick={handlers[uc.key]}
              disabled={disabled}
              className={`group relative flex items-start gap-3 p-4 bg-white rounded-2xl border-2 border-gray-100 ${uc.borderHover} shadow-sm hover:shadow-lg transition-all duration-300 text-left disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden`}
            >
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                <div className={`absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br ${uc.gradient} rounded-full opacity-5 blur-2xl`} />
              </div>

              <div className={`relative flex-shrink-0 w-10 h-10 ${uc.iconBg} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                <Icon size={20} className={uc.iconColor} />
              </div>

              <div className="relative flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <h4 className="font-extrabold text-gray-900 text-sm font-display">
                    {uc.title}
                  </h4>
                  <ArrowRight size={12} className="text-gray-300 group-hover:text-gray-500 group-hover:translate-x-1 transition-all duration-300 flex-shrink-0" />
                </div>
                <p className="text-xs text-gray-500 mt-0.5 leading-relaxed line-clamp-2">
                  {uc.subtitle}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
